import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { CheckCircle, AlertCircle, Loader2, Download } from 'lucide-react';
import { GlassButton } from '../ui';
import './PairingWizard.css';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface PairingWizardProps {
  onConnected: (site: { site_id: string; site_url: string }) => void;
  onCancel: () => void;
  embedded?: boolean; // true = inline div, false = modal overlay (default)
}

type WizardStep = 'generate' | 'waiting' | 'connected' | 'expired';

const API = process.env.REACT_APP_API_URL || '/api';

function authHeader() {
  const token = localStorage.getItem('access_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/** Normalize FastAPI error detail — may be a string or a Pydantic validation error array */
const getApiError = (e: any, fallback: string): string => {
  const detail = e?.response?.data?.detail;
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map((d: any) => d?.msg ?? JSON.stringify(d)).join('; ');
  }
  return fallback;
};

function formatCountdown(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const PairingWizard: React.FC<PairingWizardProps> = ({
  onConnected,
  onCancel,
  embedded = false,
}) => {
  const [step, setStep] = useState<WizardStep>('generate');
  const [code, setCode] = useState('');
  const [siteId, setSiteId] = useState('');
  const [expiresAt, setExpiresAt] = useState<Date | null>(null);
  const [connectedUrl, setConnectedUrl] = useState('');
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');
  const [secondsLeft, setSecondsLeft] = useState(0);

  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // ---------------------------------------------------------------------------
  // Cleanup helpers
  // ---------------------------------------------------------------------------

  const clearCountdown = useCallback(() => {
    if (countdownRef.current !== null) {
      clearInterval(countdownRef.current);
      countdownRef.current = null;
    }
  }, []);

  const clearPoll = useCallback(() => {
    if (pollRef.current !== null) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  // Cleanup both on unmount
  useEffect(() => {
    return () => {
      clearCountdown();
      clearPoll();
    };
  }, [clearCountdown, clearPoll]);

  // ---------------------------------------------------------------------------
  // Countdown timer — runs only while step === 'waiting'
  // ---------------------------------------------------------------------------

  useEffect(() => {
    if (step !== 'waiting') {
      clearCountdown();
      return;
    }

    countdownRef.current = setInterval(() => {
      setSecondsLeft((prev) => {
        if (prev <= 1) {
          clearCountdown();
          setStep('expired');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      clearCountdown();
    };
  }, [step, clearCountdown]);

  // ---------------------------------------------------------------------------
  // Polling — runs only while step === 'waiting'
  // ---------------------------------------------------------------------------

  useEffect(() => {
    if (step !== 'waiting' || !code) {
      clearPoll();
      return;
    }

    const poll = async () => {
      try {
        const res = await axios.get(`${API}/wordpress/pair/status/${code}`, {
          headers: authHeader(),
        });
        const data = res.data;
        if (data.connected) {
          clearPoll();
          clearCountdown();
          setConnectedUrl(data.site_url || '');
          setStep('connected');
        } else if (data.expired) {
          clearPoll();
          clearCountdown();
          setStep('expired');
        }
      } catch {
        // Network hiccup — retry next tick
      }
    };

    // Kick off immediately, then every 3 seconds
    poll();
    pollRef.current = setInterval(poll, 3000);

    return () => {
      clearPoll();
    };
  }, [step, code, clearPoll, clearCountdown]);

  // ---------------------------------------------------------------------------
  // Actions
  // ---------------------------------------------------------------------------

  const handleGenerate = useCallback(async () => {
    setGenerating(true);
    setError('');
    try {
      const res = await axios.post(
        `${API}/wordpress/pair/initiate`,
        {},
        { headers: authHeader() }
      );
      const data = res.data;
      setCode(data.code);
      setSiteId(data.site_id);
      setExpiresAt(data.expires_at ? new Date(data.expires_at) : null);
      setSecondsLeft(900); // 15 minutes
      setStep('waiting');
    } catch (err: any) {
      setError(getApiError(err, err?.message || 'Failed to generate connection code. Please try again.'));
    } finally {
      setGenerating(false);
    }
  }, []);

  const handleReset = useCallback(() => {
    clearCountdown();
    clearPoll();
    setStep('generate');
    setCode('');
    setSiteId('');
    setExpiresAt(null);
    setConnectedUrl('');
    setError('');
    setSecondsLeft(0);
  }, [clearCountdown, clearPoll]);

  const handleContinue = useCallback(() => {
    onConnected({ site_id: siteId, site_url: connectedUrl });
  }, [onConnected, siteId, connectedUrl]);

  // A plain <a href> can't carry the Authorization header the API requires
  // (there's no session cookie — JWT lives in localStorage), so a direct
  // link click 401s with "Not authenticated". Fetch it as an authenticated
  // blob instead and trigger the save client-side — same pattern used for
  // every other authenticated download in this app (see e.g.
  // ContentPreview.tsx's handleExport).
  const [downloadingPlugin, setDownloadingPlugin] = useState(false);
  const [downloadError, setDownloadError] = useState('');

  const handleDownloadPlugin = useCallback(async () => {
    setDownloadingPlugin(true);
    setDownloadError('');
    try {
      const res = await axios.get(`${API}/wordpress/plugin/download`, {
        headers: authHeader(),
        responseType: 'blob',
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/zip' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'aime-connect.zip';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err: any) {
      setDownloadError(getApiError(err, 'Failed to download the plugin. Please try again.'));
    } finally {
      setDownloadingPlugin(false);
    }
  }, []);

  // ---------------------------------------------------------------------------
  // Step renderers
  // ---------------------------------------------------------------------------

  const renderGenerate = () => (
    <>
      <h2 className="pw-heading">Connect Your Existing WordPress Site</h2>
      <p className="pw-subtext">
        Generate a connection code, then enter it in your WordPress admin under{' '}
        <strong>Settings → AiMe Connect</strong>.
      </p>
      {error && (
        <p className="pw-subtext" style={{ color: 'var(--danger)' }}>
          {error}
        </p>
      )}
      <div className="pw-actions">
        <GlassButton variant="secondary" onClick={onCancel}>
          Cancel
        </GlassButton>
        <GlassButton variant="primary" onClick={handleGenerate} disabled={generating}>
          {generating ? (
            <>
              <Loader2 size={16} style={{ marginRight: 6, animation: 'spin 1s linear infinite' }} />
              Generating…
            </>
          ) : (
            'Generate Connection Code'
          )}
        </GlassButton>
      </div>
    </>
  );

  const renderWaiting = () => (
    <>
      <h2 className="pw-heading">Enter This Code in WordPress</h2>

      <div className="pw-code-box">
        <span className="pw-code">{code}</span>
        <span className={`pw-countdown${secondsLeft < 180 ? ' warning' : ''}`}>
          Expires in {formatCountdown(secondsLeft)}
        </span>
      </div>

      <ol className="pw-steps">
        <li>
          <span className="pw-step-num">1</span>
          <span>
            Install the AiMe Connect plugin on your WordPress site.{' '}
            <button
              type="button"
              onClick={handleDownloadPlugin}
              disabled={downloadingPlugin}
              style={{
                background: 'none', border: 'none', padding: 0, cursor: downloadingPlugin ? 'default' : 'pointer',
                color: 'var(--primary-500)', display: 'inline-flex', alignItems: 'center', gap: 4,
                font: 'inherit', textDecoration: 'underline',
              }}
            >
              {downloadingPlugin ? <Loader2 size={13} style={{ animation: 'spin 1s linear infinite' }} /> : <Download size={13} />}
              {downloadingPlugin ? 'Downloading…' : 'Download Plugin'}
            </button>
            {downloadError && (
              <span style={{ display: 'block', color: 'var(--danger)', fontSize: '0.85em', marginTop: 2 }}>
                {downloadError}
              </span>
            )}
          </span>
        </li>
        <li>
          <span className="pw-step-num">2</span>
          <span>
            In WordPress admin, go to <strong>Settings → AiMe Connect</strong>.
          </span>
        </li>
        <li>
          <span className="pw-step-num">3</span>
          <span>
            Enter the code above and click <strong>Connect</strong>.
          </span>
        </li>
      </ol>

      <div className="pw-polling">
        <Loader2 size={14} style={{ animation: 'spin 1s linear infinite', flexShrink: 0 }} />
        Waiting for connection…
      </div>

      <div className="pw-actions">
        <GlassButton variant="secondary" onClick={onCancel}>
          Cancel
        </GlassButton>
      </div>
    </>
  );

  const renderConnected = () => (
    <div className="pw-centered">
      <CheckCircle size={48} style={{ color: 'var(--success)', flexShrink: 0 }} />
      <h2 className="pw-heading" style={{ margin: 0 }}>
        WordPress Site Connected!
      </h2>
      {connectedUrl && <span className="pw-site-url">{connectedUrl}</span>}
      <div className="pw-actions" style={{ justifyContent: 'center' }}>
        <GlassButton variant="primary" onClick={handleContinue}>
          Continue →
        </GlassButton>
      </div>
    </div>
  );

  const renderExpired = () => (
    <div className="pw-centered">
      <AlertCircle size={48} style={{ color: 'var(--warning)', flexShrink: 0 }} />
      <h2 className="pw-heading" style={{ margin: 0 }}>
        Code Expired
      </h2>
      <p className="pw-subtext" style={{ margin: 0 }}>
        The connection code has expired. Generate a new one to try again.
      </p>
      <div className="pw-actions" style={{ justifyContent: 'center' }}>
        <GlassButton variant="secondary" onClick={onCancel}>
          Cancel
        </GlassButton>
        <GlassButton variant="primary" onClick={handleReset}>
          Generate New Code
        </GlassButton>
      </div>
    </div>
  );

  const content = (
    <>
      {step === 'generate' && renderGenerate()}
      {step === 'waiting' && renderWaiting()}
      {step === 'connected' && renderConnected()}
      {step === 'expired' && renderExpired()}
    </>
  );

  // ---------------------------------------------------------------------------
  // Wrapper
  // ---------------------------------------------------------------------------

  if (embedded) {
    return <div className="pairing-wizard">{content}</div>;
  }

  return (
    <div
      className="pairing-wizard-overlay"
      onClick={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      <div className="pairing-wizard-modal">{content}</div>
    </div>
  );
};

export default PairingWizard;
